import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import {
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  Workspaces as WorkspaceIcon,
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';

interface WorkspaceInfo {
  workspaceName: string;
  defaultLocation: string;
  workspaceOwnerEmail: string;
  defaultLOD: string;
  papiURL: string;
  token: string;
}

const WorkspaceBuilder: React.FC = () => {
  const { items, getRepositoryConfigs } = useCart();
  const repositoryConfigs = getRepositoryConfigs();
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardStarted, setWizardStarted] = useState(false);
  const [workspaceInfo, setWorkspaceInfo] = useState<WorkspaceInfo>({
    workspaceName: '',
    defaultLocation: 'none',
    workspaceOwnerEmail: '',
    defaultLOD: 'detailed',
    papiURL: 'https://papi.beta.runwhen.com',
    token: '',
  });

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  const handleDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const updateWorkspaceInfo = (field: keyof WorkspaceInfo, value: string) => {
    setWorkspaceInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateWorkspaceInfoYaml = (): string => {
    const yamlContent = `# RunWhen Workspace Configuration
# Generated by RunWhen Skills Registry Workspace Builder
# Generated at: ${new Date().toISOString()}

workspaceName: "${workspaceInfo.workspaceName}"
defaultLocation: "${workspaceInfo.defaultLocation}"
workspaceOwnerEmail: "${workspaceInfo.workspaceOwnerEmail}"
defaultLOD: "${workspaceInfo.defaultLOD}"
papiURL: "${workspaceInfo.papiURL}"
token: "${workspaceInfo.token}"

# Code Collections (from your cart)
codeCollections:${repositoryConfigs.length > 0 ? repositoryConfigs.map(repo => `
  - repoURL: "${repo.git_url}"
    ref: "${repo.git_ref || 'main'}"
    name: "${repo.collection_name}"`) : `
  # No code collections selected`}
`;

    return yamlContent;
  };

  const locationOptions = [
    { value: 'none', label: 'None (Local Only)' },
    { value: 'location-01-us-west1', label: 'US West 1' },
    { value: 'location-02-us-east1', label: 'US East 1' },
    { value: 'location-03-eu-west1', label: 'EU West 1' },
    { value: 'location-04-ap-southeast1', label: 'Asia Pacific Southeast 1' },
  ];

  const lodOptions = [
    { value: 'basic', label: 'Basic - Essential information only' },
    { value: 'detailed', label: 'Detailed - Comprehensive troubleshooting info' },
    { value: 'verbose', label: 'Verbose - Maximum detail for debugging' },
  ];

  if (!wizardStarted) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <WorkspaceIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Workspace Configuration Builder
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Create a workspaceInfo.yaml file for RunWhen Local deployment. 
            This lightweight configuration focuses on workspace settings and code collections.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="body2">
              <strong>Workspace Builder vs Helm Wizard:</strong> This builder creates a simple workspaceInfo.yaml 
              file for basic RunWhen Local setup. Use the Helm Configuration Wizard for complete deployment configuration 
              including networking, security, and resource settings.
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={() => setWizardStarted(true)}
            >
              Start Workspace Builder
            </Button>
            <Typography variant="body2" color="text.secondary">
              {items.length > 0 ? `${items.length} code collections selected` : 'No code collections selected'}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Privacy Notice:</strong> This workspace builder runs entirely in your browser. 
          No data is stored on our servers. All generated configurations remain on your device.
        </Typography>
      </Alert>

      {/* Wizard Step Navigation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WorkspaceIcon color="primary" />
              <Typography variant="h6">
                Workspace Configuration Builder
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Step {wizardStep + 1} of 4
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => setWizardStep(Math.max(0, wizardStep - 1))}
                disabled={wizardStep === 0}
              >
                Previous
              </Button>
              {wizardStep < 3 && (
                <Button
                  variant="contained"
                  endIcon={wizardStep === 2 ? <SettingsIcon /> : <ArrowForwardIcon />}
                  onClick={() => setWizardStep(Math.min(3, wizardStep + 1))}
                >
                  {wizardStep === 2 ? 'Generate Configuration' : 'Next'}
                </Button>
              )}
              {wizardStep === 3 && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(generateWorkspaceInfoYaml(), 'workspaceInfo.yaml')}
                >
                  Download & Finish
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[0, 1, 2, 3].map((step) => (
                <Box
                  key={step}
                  sx={{
                    flex: 1,
                    height: 4,
                    backgroundColor: step <= wizardStep ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                  }}
                />
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Step 1: Basic Workspace Information */}
      {wizardStep === 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Step 1: Workspace Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure your RunWhen workspace basic settings
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
              <TextField
                fullWidth
                label="Workspace Name"
                value={workspaceInfo.workspaceName}
                onChange={(e) => updateWorkspaceInfo('workspaceName', e.target.value)}
                placeholder="my-workspace"
                helperText="A unique name for your RunWhen workspace"
                required
              />
              
              <TextField
                fullWidth
                label="Workspace Owner Email"
                type="email"
                value={workspaceInfo.workspaceOwnerEmail}
                onChange={(e) => updateWorkspaceInfo('workspaceOwnerEmail', e.target.value)}
                placeholder="admin@company.com"
                helperText="Email address of the workspace administrator"
                required
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Location and Detail Settings */}
      {wizardStep === 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Step 2: Location & Detail Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure where your workspace data is processed and the level of detail for troubleshooting
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
              <FormControl fullWidth>
                <InputLabel>Default Location</InputLabel>
                <Select
                  value={workspaceInfo.defaultLocation}
                  label="Default Location"
                  onChange={(e) => updateWorkspaceInfo('defaultLocation', e.target.value)}
                >
                  {locationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>Level of Detail (LOD)</InputLabel>
                <Select
                  value={workspaceInfo.defaultLOD}
                  label="Level of Detail (LOD)"
                  onChange={(e) => updateWorkspaceInfo('defaultLOD', e.target.value)}
                >
                  {lodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Platform Integration */}
      {wizardStep === 2 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Step 3: Platform Integration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure connection to the RunWhen platform (optional)
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
              <TextField
                fullWidth
                label="Platform API URL"
                value={workspaceInfo.papiURL}
                onChange={(e) => updateWorkspaceInfo('papiURL', e.target.value)}
                placeholder="https://papi.beta.runwhen.com"
                helperText="RunWhen Platform API endpoint"
              />
              
              <TextField
                fullWidth
                label="Authentication Token"
                type="password"
                value={workspaceInfo.token}
                onChange={(e) => updateWorkspaceInfo('token', e.target.value)}
                placeholder="your-auth-token"
                helperText="Optional: Token for RunWhen Platform integration"
              />

              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Platform Integration:</strong> These settings are optional. Leave the token empty 
                  to run RunWhen Local in standalone mode without platform connectivity.
                </Typography>
              </Alert>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Generated Configuration */}
      {wizardStep === 3 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Step 4: Generated Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your workspaceInfo.yaml file is ready! Copy or download it for RunWhen Local.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon color="primary" />
                <Typography variant="h6">
                  workspaceInfo.yaml
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => handleCopyToClipboard(generateWorkspaceInfoYaml())}
                >
                  Copy YAML
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload(generateWorkspaceInfoYaml(), 'workspaceInfo.yaml')}
                >
                  Download
                </Button>
              </Box>
            </Box>

            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Configuration Complete!</strong> Your workspaceInfo.yaml file has been generated. 
                Place this file in your RunWhen Local shared directory or mount it as a volume.
              </Typography>
            </Alert>

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Editor
                height="400px"
                defaultLanguage="yaml"
                value={generateWorkspaceInfoYaml()}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 14,
                  lineNumbers: 'on',
                  renderWhitespace: 'boundary',
                  wordWrap: 'on',
                  theme: 'vs-light',
                  automaticLayout: true,
                  folding: true,
                  lineDecorationsWidth: 10,
                  lineNumbersMinChars: 3,
                  glyphMargin: false
                }}
                loading={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading YAML editor...
                    </Typography>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Next Steps:
              </Typography>
              <Typography variant="body2" component="div">
                1. Save the workspaceInfo.yaml file to your local machine<br/>
                2. <strong>Docker:</strong> Mount as volume: <code>-v /path/to/workspaceInfo.yaml:/app/shared/workspaceInfo.yaml</code><br/>
                3. <strong>Kubernetes:</strong> Create ConfigMap: <code>kubectl create configmap workspace-info --from-file=workspaceInfo.yaml</code><br/>
                4. <strong>Helm:</strong> Use with values.yaml: <code>workspaceInfo.configMapName: workspace-info</code>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default WorkspaceBuilder;
